import { Card } from 'primereact/card'
import { DataTable } from 'primereact/datatable'
import { DataTableRowClickEvent } from 'primereact/datatable'
import { Column as TableColumn } from 'primereact/column'
import { useNavigate } from 'react-router-dom'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useSensorContext } from '../contexts/SensorContext'
import { useUserContext } from '../contexts/UserContext'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import { TagIdCopyButton } from '../common/pure/CopyButton'
import ContextHeader from '../common/ContextHeader'
import DateDisplay from '../common/pure/DateDisplay'
import Heading from '../common/pure/Heading'
import TablePagination from '../table/TablePagination'
import { ArchiveButton, RestoreButton, TableActionContainer } from '../table/TableButton'
import {
    getFilterableDateRangeColumnProps,
    getFilterableEnumColumnProps,
    getSortableColumnProps,
    getSortableTableProps,
} from '../table/table-props'
import Sensor from '../../models/Sensor'
import { buildNavigationLink } from '../../tools/link'
import { SensorFrequencySettings, SensorSizeSettings } from '../../tools/settings'
import t, { _ta } from '../../tools/t'
import { Row } from '../common/pure/FlexContainer'
import { TooltipAvatar } from '../common/ProfileAvatar'

export default function AllSensorsScreen() {
    const navigate = useNavigate()
    const { sensors, archiveSensor, restoreSensor } = useSensorContext()
    const { me } = useUserContext()
    const canWriteHardware = me?.hasPermission(undefined, PermissionSuffix.HardwareWrite)

    const navigateToSensor = (event: DataTableRowClickEvent) => {
        const sensor = event.data as Sensor
        navigate(buildNavigationLink('sensors', sensor.id))
    }

    const onArchiveSensor = async (sensor: Sensor) => {
        if (!confirm('Are you sure you want to archive sensor:\n' + sensor.serialNo)) return
        await archiveSensor(sensor.id)
    }

    const onRestoreSensor = async (sensor: Sensor) => {
        await restoreSensor(sensor.id)
    }

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <ContextHeader>
                        <Heading standalone size={2}>
                            {t('common_sensors')}
                        </Heading>
                    </ContextHeader>
                    <Card>
                        <DataTable
                            value={sensors.items}
                            rowClassName={(s: Sensor) =>
                                s.isArchived ? 'archived-table-row' : undefined
                            }
                            rowHover={true}
                            emptyMessage={t('hardware_empty')}
                            onRowClick={navigateToSensor}
                            {...getSortableTableProps(sensors)}
                        >
                            <TableColumn
                                header={t('common_tag')}
                                body={(s: Sensor) => <TagIdCopyButton tag={s.currentTag} />}
                            />

                            <TableColumn
                                header={t('sensor_field_serial')}
                                body={(s: Sensor) => s.serialNo}
                            />

                            <TableColumn
                                header={t('sensor_field_size')}
                                body={(s: Sensor) => _ta(s, 'size')}
                                {...getFilterableEnumColumnProps(
                                    'size',
                                    sensors,
                                    SensorSizeSettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('sensor_field_frequency')}
                                body={(s: Sensor) => _ta(s, 'frequency')}
                                {...getFilterableEnumColumnProps(
                                    'frequency',
                                    sensors,
                                    SensorFrequencySettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('sensor_field_times_used')}
                                body={(s: Sensor) => s.timesUsed}
                            />

                            <TableColumn
                                header={t('life_cycle_field_last_used_summary')}
                                body={(s: Sensor) => (
                                    <Row gap={0.5} alignment="center">
                                        <TooltipAvatar
                                            position="bottom"
                                            id={s.lastUsedBy ?? ''}
                                            target={s.id + s.lastUsedBy}
                                        />
                                        <DateDisplay value={s.lastUsedAt} target={s.id} />
                                    </Row>
                                )}
                            />

                            <TableColumn
                                header={t('life_cycle_field_created_at')}
                                body={(s: Sensor) => (
                                    <DateDisplay value={s.createdAt} target={s.id} />
                                )}
                                {...getSortableColumnProps('createdAt')}
                                {...getFilterableDateRangeColumnProps('createdAt', sensors)}
                            />

                            {canWriteHardware && (
                                <TableColumn
                                    body={(s: Sensor) => (
                                        <TableActionContainer>
                                            {s.isArchived ? (
                                                <RestoreButton
                                                    target={s.id}
                                                    onClick={() => onRestoreSensor(s)}
                                                />
                                            ) : (
                                                <ArchiveButton
                                                    target={s.id}
                                                    onClick={() => onArchiveSensor(s)}
                                                />
                                            )}
                                        </TableActionContainer>
                                    )}
                                />
                            )}
                        </DataTable>
                        <TablePagination data={sensors} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
